import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Database, RefreshCw, Search, TriangleAlert } from "lucide-react";
import { useMemo, useState } from "react";
import { EmployeeDetailDrawer } from "../components/EmployeeDetailDrawer.js";
import { AppLayout } from "../components/layout/AppLayout.js";
import { Header } from "../components/layout/Header.js";
import { KpiInfo } from "../components/ui/KpiInfo.js";
import { SortKopf } from "../components/ui/SortKopf.js";
import { useTabellenSortierung } from "../hooks/useTabellenSortierung.js";
import { type MeinTeamRow, type WirtschaftAmpelLevel, wirtschaftApi } from "../lib/api.js";
import type { SortSpalten } from "../lib/tabellen-sortierung.js";
import { berechneTeamKennzahlen } from "../lib/team-kennzahlen.js";
import { nachRisikoScore, nachWirtschaftsAmpel } from "../lib/team-reihenfolge.js";

// ── W26 Ebene 1: Mein Team — eine Liste, zwei Ampeln (Bindung + Wirtschaft) ───
// Der Perspektiven-Umschalter steuert die Kennzahl-Spalten und seit 10.09.2026
// auch die Standardreihenfolge (lib/team-reihenfolge.ts); die beiden
// Ampel-Punkte sind in jeder Perspektive sichtbar (Zielbild 03.08., Punkt 6).

// ── Formatierung ──────────────────────────────────────────────────────────────

const fmtEur = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
  maximumFractionDigits: 0,
});

const MONTH_LONG: Record<string, string> = {
  "01": "Januar",
  "02": "Februar",
  "03": "März",
  "04": "April",
  "05": "Mai",
  "06": "Juni",
  "07": "Juli",
  "08": "August",
  "09": "September",
  "10": "Oktober",
  "11": "November",
  "12": "Dezember",
};

/** "2026-07" → "Juli 2026" */
function fmtMonthLong(ym: string): string {
  const [year, mm] = ym.split("-");
  return `${MONTH_LONG[mm] ?? mm} ${year}`;
}

// ── Ampel-Darstellung ─────────────────────────────────────────────────────────

const LEVEL_RANK: Record<WirtschaftAmpelLevel, number> = { ok: 0, warning: 1, critical: 2 };

const LEVEL_COLOR: Record<WirtschaftAmpelLevel, string> = {
  ok: "var(--risk-low)",
  warning: "var(--risk-medium)",
  critical: "var(--risk-high)",
};

/** Farbiger TEXT nutzt die kontrastgeprüften -text-Varianten (≥ 4.5:1). */
const LEVEL_TEXT_COLOR: Record<WirtschaftAmpelLevel, string> = {
  ok: "var(--risk-low-text)",
  warning: "var(--risk-medium-text)",
  critical: "var(--risk-high-text)",
};

const LEVEL_LABEL: Record<WirtschaftAmpelLevel, string> = {
  ok: "OK",
  warning: "Warnung",
  critical: "Kritisch",
};

const AMPEL_ORDER: ReadonlyArray<WirtschaftAmpelLevel> = ["ok", "warning", "critical"];

function AmpelDot({ level, title }: { level: WirtschaftAmpelLevel; title: string }) {
  return (
    <span title={title} className="inline-flex items-center gap-1 shrink-0">
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: LEVEL_COLOR[level],
        }}
      />
      {/* Farbe steht nie allein: bei „kritisch" zusätzlich ein Warn-Icon */}
      {level === "critical" && (
        <TriangleAlert size={11} aria-hidden style={{ color: "var(--risk-high)" }} />
      )}
    </span>
  );
}

function bindungTitle(row: MeinTeamRow): string {
  const parts = [`Bindung: ${LEVEL_LABEL[row.bindung.level]}`];
  if (row.bindung.score != null)
    parts.push(`Risiko-Score ${Math.round(row.bindung.score * 100)} %`);
  if (row.bindung.topSignal) parts.push(`Top-Signal: ${row.bindung.topSignal}`);
  return parts.join(" · ");
}

function wirtschaftTitle(row: MeinTeamRow): string {
  const head = `Wirtschaftlichkeit: ${LEVEL_LABEL[row.wirtschaft.level]}`;
  if (row.wirtschaft.alerts.length === 0) return head;
  return `${head}\n${row.wirtschaft.alerts.map((a) => `• ${a.message}`).join("\n")}`;
}

/**
 * Kritischste sichtbare Stufe der Zeile (für die Sortierung). Bei bestätigten
 * Kündigungen zählt nur die Wirtschafts-Ampel — die Bindungs-Ampel wird dort
 * durch das Badge ersetzt und soll die Liste nicht mehr treiben.
 */
function rowSeverity(row: MeinTeamRow): number {
  const bindung = row.bindung.isConfirmedLeaving ? 0 : LEVEL_RANK[row.bindung.level];
  return Math.max(bindung, LEVEL_RANK[row.wirtschaft.level]);
}

// ── Perspektiven ──────────────────────────────────────────────────────────────

type Perspektive = "bindung" | "wirtschaft";

const PERSPEKTIVEN: ReadonlyArray<{ key: Perspektive; label: string }> = [
  { key: "bindung", label: "Bindung" },
  { key: "wirtschaft", label: "Wirtschaftlichkeit" },
];

// Standardreihenfolge je Ansicht (Entscheidung Marie 10.09.2026). Die Regeln
// stehen in lib/team-reihenfolge.ts, weil die Wirtschaftlichkeits-Seite
// dieselbe Wirtschafts-Reihenfolge nutzt.
const STANDARD: Record<Perspektive, (a: MeinTeamRow, b: MeinTeamRow) => number> = {
  bindung: nachRisikoScore,
  wirtschaft: nachWirtschaftsAmpel,
};

// Beide Perspektiven in EINER Definition. Spalten, die gerade nicht sichtbar
// sind, stehen einfach nicht in der Kopfzeile — die Definition stoert nicht.
const SORT_TEAM: SortSpalten<MeinTeamRow> = {
  name: { wert: (r) => r.name, typ: "text" },
  abteilung: { wert: (r) => r.department ?? r.team, typ: "text" },
  ampeln: { wert: rowSeverity, typ: "zahl" },
  score: { wert: (r) => r.bindung.score, typ: "zahl" },
  signal: { wert: (r) => r.bindung.topSignal, typ: "text" },
  auslastung: { wert: (r) => r.wirtschaft.utilizationAvg3m, typ: "zahl" },
  umsatz: { wert: (r) => r.wirtschaft.umsatz12m, typ: "zahl" },
  marge: { wert: (r) => r.wirtschaft.deckungsbeitragPct12m, typ: "zahl" },
};

// Spaltenraster je Perspektive (Name · Abteilung · Ampeln · Kennzahlen)
const GRID_COLS: Record<Perspektive, string> = {
  bindung: "200px 1fr 150px 100px 1.2fr",
  wirtschaft: "200px 1fr 150px 110px 130px 90px",
};

// ── Seite ─────────────────────────────────────────────────────────────────────

export function MeinTeam() {
  const [perspektive, setPerspektive] = useState<Perspektive>("bindung");
  const [search, setSearch] = useState("");
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<number | null>(null);

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ["mein-team"],
    queryFn: wirtschaftApi.getTeam,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const total = data?.rows.length ?? 0;

  const { klick, sortiere, richtungVon, zuruecksetzen } = useTabellenSortierung(
    SORT_TEAM,
    STANDARD[perspektive],
  );

  // Ohne Klick auf einen Spaltenkopf gilt die Standardreihenfolge der Ansicht:
  // Bindung → höchster Risiko-Score oben, Wirtschaftlichkeit → höchste Ampel oben.
  const rows = useMemo(() => {
    const all = data?.rows ?? [];
    const q = search.trim().toLowerCase();
    const filtered = q === "" ? all : all.filter((r) => r.name.toLowerCase().includes(q));
    return sortiere(filtered);
  }, [data, search, sortiere]);

  // Summary-Kacheln — alles aus den bereits geladenen Zeilen, keine neuen Requests.
  // Die Regeln stehen seit dem 16.09.2026 in lib/team-kennzahlen.ts (mit Tests).
  const summary = useMemo(() => berechneTeamKennzahlen(rows), [rows]);
  const verteilung = perspektive === "bindung" ? summary.bindungAmpel : summary.wirtschaftAmpel;

  const gridCols = GRID_COLS[perspektive];

  return (
    <AppLayout>
      <Header
        title="Mein Team"
        subtitle="Bindung & Wirtschaftlichkeit — zwei Ampeln je Mitarbeiter"
      />
      <div className="flex-1 overflow-hidden flex flex-col px-6 py-5 page-enter">
        {/* Summary-Zeile — Teamgröße, Risikolage, Ø Auslastung, Auffällig */}
        {!isLoading && !isError && data && (
          <div className="stagger grid grid-cols-4 gap-3 mb-4">
            {/* Teamgröße */}
            <div className="card p-4 relative">
              <div className="flex items-center justify-between gap-1 mb-1.5">
                <p
                  className="text-[9px] font-bold tracking-widest uppercase leading-tight"
                  style={{ color: "var(--text-muted)" }}
                >
                  Teamgröße
                </p>
                <KpiInfo schluessel="teamGroesse" label="Teamgröße" />
              </div>
              <div
                className="text-2xl font-bold font-mono-data tabular-nums"
                style={{ color: "var(--text-primary)" }}
              >
                {total}
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                Mitarbeiter
              </p>
            </div>

            {/* Risikolage — Ampelverteilung als segmentierter Balken. Folgt seit dem
                16.09.2026 dem Schalter Bindung / Wirtschaftlichkeit (Marie: „C"):
                Vorher zählte sie immer die Wirtschafts-Ampel, der Erklärtext
                beschrieb aber die Bindungs-Ampel. */}
            <div className="card p-4 relative">
              <div className="flex items-center justify-between gap-1 mb-1.5">
                <p
                  className="text-[9px] font-bold tracking-widest uppercase leading-tight"
                  style={{ color: "var(--text-muted)" }}
                >
                  Risikolage · {perspektive === "bindung" ? "Bindung" : "Wirtschaft"}
                </p>
                {perspektive === "bindung" ? (
                  <KpiInfo schluessel="teamRisikolageBindung" label="Risikolage Bindung" />
                ) : (
                  <KpiInfo schluessel="teamRisikolageWirtschaft" label="Risikolage Wirtschaft" />
                )}
              </div>
              {rows.length > 0 ? (
                <div className="flex" style={{ gap: 2, height: 8, marginTop: 6 }}>
                  {AMPEL_ORDER.filter((lvl) => verteilung[lvl] > 0).map((lvl) => (
                    <div
                      key={lvl}
                      title={`${LEVEL_LABEL[lvl]}: ${verteilung[lvl]}`}
                      style={{
                        flexGrow: verteilung[lvl],
                        flexBasis: 0,
                        borderRadius: "var(--radius-pill)",
                        background: LEVEL_COLOR[lvl],
                      }}
                    />
                  ))}
                </div>
              ) : (
                <div
                  style={{
                    height: 8,
                    marginTop: 6,
                    borderRadius: "var(--radius-pill)",
                    background: "var(--bg-inset)",
                  }}
                />
              )}
              {/* Zahlen-Legende mit Labels — Farbe steht nie allein */}
              <div
                className="flex items-center gap-2.5 mt-2 text-[10px]"
                style={{ color: "var(--text-secondary)" }}
              >
                {AMPEL_ORDER.map((lvl) => (
                  <span key={lvl} className="inline-flex items-center gap-1 whitespace-nowrap">
                    <span
                      className="inline-block w-1.5 h-1.5 rounded-full"
                      style={{ background: LEVEL_COLOR[lvl] }}
                    />
                    <span className="font-mono-data tabular-nums">{verteilung[lvl]}</span>
                    {LEVEL_LABEL[lvl]}
                  </span>
                ))}
              </div>
            </div>

            {/* Ø Auslastung 3M */}
            <div className="card p-4 relative">
              <div className="flex items-center justify-between gap-1 mb-1.5">
                <p
                  className="text-[9px] font-bold tracking-widest uppercase leading-tight"
                  style={{ color: "var(--text-muted)" }}
                >
                  Ø Auslastung 3M
                </p>
                <KpiInfo schluessel="teamAuslastung3m" label="Ø Auslastung 3M" />
              </div>
              <div
                className="text-2xl font-bold font-mono-data tabular-nums"
                style={{ color: "var(--text-primary)" }}
              >
                {summary.utilizationAvg != null ? `${summary.utilizationAvg} %` : "—"}
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                über bewertete Mitarbeiter
              </p>
            </div>

            {/* Auffällig — mind. eine nicht-grüne Ampel */}
            <div className="card p-4 relative">
              <div className="flex items-center justify-between gap-1 mb-1.5">
                <p
                  className="text-[9px] font-bold tracking-widest uppercase leading-tight"
                  style={{ color: "var(--text-muted)" }}
                >
                  Auffällig
                </p>
                <KpiInfo schluessel="teamAuffaellig" label="Auffällig" />
              </div>
              <div className="flex items-center gap-1.5">
                <div
                  className="text-2xl font-bold font-mono-data tabular-nums"
                  style={{ color: "var(--text-primary)" }}
                >
                  {summary.auffaellig}
                </div>
                {summary.auffaellig > 0 && (
                  <TriangleAlert size={13} aria-hidden style={{ color: "var(--risk-medium)" }} />
                )}
              </div>
              <p className="text-[10px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                mind. eine Ampel nicht grün
              </p>
            </div>
          </div>
        )}

        {/* Toolbar */}
        <div className="flex items-center gap-3 mb-4">
          {/* Suche (nur Name) */}
          <div
            className="flex items-center gap-2 px-3 py-2 rounded flex-1 max-w-xs"
            style={{ background: "var(--bg-card)", border: "1px solid var(--border)" }}
          >
            <Search size={13} style={{ color: "var(--text-muted)" }} />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name suchen…"
              className="bg-transparent text-sm outline-none w-full"
              style={{ color: "var(--text-primary)" }}
            />
          </div>

          {/* Perspektiven-Umschalter */}
          <div className="segmented shrink-0">
            {PERSPEKTIVEN.map(({ key, label }) => (
              <button
                key={key}
                type="button"
                data-active={perspektive === key}
                onClick={() => {
                  setPerspektive(key);
                  // Schalter umlegen = Standard der neuen Ansicht. Sonst bliebe
                  // eine Sortierung nach einer Spalte aktiv, die gar nicht mehr
                  // zu sehen ist (z. B. Risiko-Score in der Wirtschafts-Ansicht).
                  zuruecksetzen();
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Refresh */}
          <button
            type="button"
            onClick={() => void refetch()}
            disabled={isFetching}
            className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded text-xs transition-colors"
            style={{
              border: "1px solid var(--border)",
              color: "var(--text-muted)",
              background: "transparent",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = "var(--border-light)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = "var(--border)";
            }}
          >
            <RefreshCw size={11} className={isFetching ? "animate-spin" : ""} />
            {isFetching ? "Lädt…" : "Aktualisieren"}
          </button>
        </div>

        {/* Demo-Hinweis (Etappe 0) — dezenter Chip statt breitem Balken */}
        {data?.dataBasis === "demo" && (
          <div className="mb-4">
            <span
              className="chip"
              style={{
                background: "var(--accent-gold-dim)",
                border: "1px solid var(--accent-gold-dim)",
                color: "var(--text-secondary)",
              }}
            >
              <Database size={11} aria-hidden style={{ color: "var(--accent-gold)" }} />
              Demo-Datenbasis (Etappe 0) — echte Salesforce-Zahlen folgen mit dem Datenfundament
            </span>
          </div>
        )}

        {/* Teamliste */}
        <div className="card flex-1 overflow-hidden flex flex-col">
          {/* Kopfzeile */}
          <div
            className="grid text-xs font-medium px-4 py-3"
            style={{
              gridTemplateColumns: gridCols,
              gap: "0 12px",
              color: "var(--text-muted)",
              borderBottom: "1px solid var(--border)",
            }}
          >
            <SortKopf
              label="Mitarbeiter"
              spalte="name"
              zusatz="↗ Detail"
              richtung={richtungVon("name")}
              onKlick={klick}
            />
            <SortKopf
              label="Abteilung"
              spalte="abteilung"
              richtung={richtungVon("abteilung")}
              onKlick={klick}
            />
            <SortKopf
              label="Bindung · Wirtschaft"
              spalte="ampeln"
              info="teamSpalteAmpeln"
              richtung={richtungVon("ampeln")}
              onKlick={klick}
            />
            {perspektive === "bindung" ? (
              <>
                <SortKopf
                  label="Risiko-Score"
                  spalte="score"
                  info="teamSpalteRisikoScore"
                  rechts
                  richtung={richtungVon("score")}
                  onKlick={klick}
                />
                <SortKopf
                  label="Top-Signal"
                  spalte="signal"
                  info="teamSpalteTopSignal"
                  richtung={richtungVon("signal")}
                  onKlick={klick}
                />
              </>
            ) : (
              <>
                <SortKopf
                  label="Auslastung Ø 3M"
                  spalte="auslastung"
                  info="utilizationAvg3m"
                  rechts
                  richtung={richtungVon("auslastung")}
                  onKlick={klick}
                />
                <SortKopf
                  label="Umsatz 12M"
                  spalte="umsatz"
                  info="umsatz12m"
                  rechts
                  richtung={richtungVon("umsatz")}
                  onKlick={klick}
                />
                <SortKopf
                  label="Marge"
                  spalte="marge"
                  info="tabelleMitarbeiterMarge"
                  rechts
                  richtung={richtungVon("marge")}
                  onKlick={klick}
                />
              </>
            )}
          </div>

          {/* Inhalt */}
          <div className="flex-1 overflow-y-auto">
            {isLoading && (
              <div className="flex flex-col gap-3 px-4 py-4" aria-label="Teamdaten werden geladen">
                {["s1", "s2", "s3", "s4"].map((k) => (
                  <div key={k} className="skeleton" style={{ height: 42 }} />
                ))}
              </div>
            )}

            {isError && (
              <div className="flex items-center justify-center h-32 gap-2 px-6">
                <AlertCircle size={15} style={{ color: "var(--risk-high)" }} />
                <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                  {error instanceof Error ? error.message : "Fehler beim Laden der Daten"}
                </p>
              </div>
            )}

            {!isLoading && !isError && rows.length === 0 && (
              <div
                className="flex items-center justify-center h-32 text-sm"
                style={{ color: "var(--text-muted)" }}
              >
                Keine Mitarbeiter gefunden
              </div>
            )}

            {!isLoading &&
              !isError &&
              rows.map((row) => (
                <div
                  key={row.id}
                  className="grid items-center px-4 py-3 row-hover cursor-pointer"
                  style={{
                    gridTemplateColumns: gridCols,
                    gap: "0 12px",
                    borderBottom: "1px solid var(--border)",
                  }}
                  onClick={() => setSelectedEmployeeId(row.id)}
                >
                  {/* Name + Position */}
                  <div className="min-w-0">
                    <div
                      className="text-xs font-medium truncate"
                      style={{ color: "var(--text-primary)" }}
                    >
                      {row.name}
                    </div>
                    {row.position && (
                      <div
                        className="text-[10px] mt-0.5 truncate"
                        style={{ color: "var(--text-muted)" }}
                      >
                        {row.position}
                      </div>
                    )}
                  </div>

                  {/* Abteilung + Team */}
                  <div className="min-w-0">
                    <div className="text-sm truncate" style={{ color: "var(--text-secondary)" }}>
                      {row.department ?? "—"}
                    </div>
                    {row.team && (
                      <div
                        className="text-[10px] mt-0.5 truncate"
                        style={{ color: "var(--accent)", opacity: 0.8 }}
                      >
                        {row.team}
                      </div>
                    )}
                  </div>

                  {/* Zwei Ampeln — Bindung (bzw. Kündigungs-Badge) + Wirtschaft */}
                  <div className="flex items-center gap-2">
                    {row.bindung.isConfirmedLeaving ? (
                      <span
                        className="chip"
                        style={{
                          background: "var(--bg-hover)",
                          color: "var(--text-muted)",
                          border: "1px solid var(--border)",
                        }}
                      >
                        Kündigung bestätigt
                      </span>
                    ) : (
                      <AmpelDot level={row.bindung.level} title={bindungTitle(row)} />
                    )}
                    <AmpelDot level={row.wirtschaft.level} title={wirtschaftTitle(row)} />
                  </div>

                  {perspektive === "bindung" ? (
                    <>
                      {/* Risiko-Score */}
                      <span
                        className="font-mono-data text-xs text-right tabular-nums"
                        style={{
                          color:
                            row.bindung.score == null
                              ? "var(--text-muted)"
                              : row.bindung.level === "ok"
                                ? "var(--text-primary)"
                                : LEVEL_TEXT_COLOR[row.bindung.level],
                        }}
                      >
                        {row.bindung.score != null
                          ? `${Math.round(row.bindung.score * 100)} %`
                          : "—"}
                      </span>
                      {/* Top-Signal */}
                      <div className="min-w-0">
                        {row.bindung.topSignal ? (
                          <span
                            className="inline-block max-w-full text-[10px] px-1.5 py-0.5 rounded truncate align-middle"
                            style={{
                              background: "var(--bg-hover)",
                              color: "var(--text-muted)",
                              border: "1px solid var(--border)",
                            }}
                          >
                            {row.bindung.topSignal}
                          </span>
                        ) : (
                          <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                            —
                          </span>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Auslastung Ø 3M */}
                      <span
                        className="font-mono-data text-xs text-right tabular-nums"
                        style={{
                          color:
                            row.wirtschaft.utilizationAvg3m == null
                              ? "var(--text-muted)"
                              : "var(--text-primary)",
                        }}
                      >
                        {row.wirtschaft.utilizationAvg3m != null
                          ? `${row.wirtschaft.utilizationAvg3m} %`
                          : "—"}
                      </span>
                      {/* Umsatz 12M */}
                      <span
                        className="font-mono-data text-xs text-right tabular-nums"
                        style={{ color: "var(--text-primary)" }}
                      >
                        {fmtEur.format(row.wirtschaft.umsatz12m)}
                      </span>
                      {/* Marge = DB-Marge: (Umsatz − Kosten) ÷ Umsatz über 12M.
                          Maries Ansage vom 04.09.2026 — wenn sie „Marge“ sagt, ist
                          immer diese gemeint; die Stundensatz-Marge lebt nur noch
                          in Ampelregel 2 weiter. */}
                      <span
                        className="font-mono-data text-xs text-right tabular-nums"
                        title={
                          row.wirtschaft.deckungsbeitragPct12m == null
                            ? "Keine Marge berechenbar — kein Umsatz im Zeitraum"
                            : undefined
                        }
                        style={{
                          color:
                            row.wirtschaft.deckungsbeitragPct12m == null
                              ? "var(--text-muted)"
                              : row.wirtschaft.deckungsbeitragPct12m < 0
                                ? "var(--risk-high-text)"
                                : "var(--text-primary)",
                        }}
                      >
                        {row.wirtschaft.deckungsbeitragPct12m != null
                          ? `${row.wirtschaft.deckungsbeitragPct12m} %`
                          : "—"}
                      </span>
                    </>
                  )}
                </div>
              ))}
          </div>

          {/* Fußzeile */}
          {!isLoading && !isError && data && (
            <div
              className="px-4 py-2.5 flex items-center justify-between text-xs"
              style={{ borderTop: "1px solid var(--border)", color: "var(--text-muted)" }}
            >
              <span>
                {rows.length} von {total} Mitarbeitern
              </span>
              <span>Stand: letzter abgeschlossener Monat {fmtMonthLong(data.lastClosedMonth)}</span>
            </div>
          )}
        </div>
      </div>
      {/*
        key={selectedEmployeeId} ist Pflicht (auch in RetentionRadar und Cockpit):
        Der Drawer rendert bei employeeId === null nur `null`, wird aber nie
        unmountet — ohne key überlebt sein lokaler State den Personenwechsel.
        Konkret bliebe ein offenes, ungespeichertes Maßnahmen-Formular samt
        eingetippter Notiz über Person A stehen und ließe sich auf Person B
        speichern. Der key setzt beim Personenwechsel den kompletten lokalen
        State strukturell zurück — feldweises Zurücksetzen im Drawer verrottet,
        sobald jemand einen weiteren State ergänzt.
      */}
      <EmployeeDetailDrawer
        key={selectedEmployeeId}
        employeeId={selectedEmployeeId}
        onClose={() => setSelectedEmployeeId(null)}
      />
    </AppLayout>
  );
}
